import React, { useState, useEffect } from 'react';
import { Gift, Star, Award, Coins, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import api from '../utils/api';
import { toast } from 'sonner';

const LoyaltyProgram = () => {
  const [loyalty, setLoyalty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [redeemPoints, setRedeemPoints] = useState(100);
  const [redeeming, setRedeeming] = useState(false);

  useEffect(() => {
    fetchLoyaltyStatus();
  }, []);

  const fetchLoyaltyStatus = async () => {
    try {
      const response = await api.get('/api/loyalty');
      setLoyalty(response.data);
    } catch (error) {
      setLoyalty({
        points: 750,
        tier: 'silver',
        total_earned: 1250,
        total_redeemed: 500,
        discount: 5,
        tiers: {
          bronze: { min_points: 0, discount: 0 },
          silver: { min_points: 500, discount: 5 },
          gold: { min_points: 2000, discount: 10 },
          platinum: { min_points: 5000, discount: 15 }
        },
        next_tier: { name: 'gold', points_needed: 1250 }
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRedeem = async () => {
    if (redeemPoints < 100) {
      toast.error('Minimum redemption is 100 points');
      return;
    }
    if (redeemPoints > loyalty.points) {
      toast.error('Insufficient points');
      return;
    }
    setRedeeming(true);
    try {
      const response = await api.post(`/api/loyalty/redeem?points=${redeemPoints}`);
      toast.success(response.data.message);
      fetchLoyaltyStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to redeem points');
    } finally {
      setRedeeming(false);
    }
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case 'bronze': return 'from-amber-700 to-amber-600';
      case 'silver': return 'from-gray-400 to-gray-300';
      case 'gold': return 'from-yellow-500 to-yellow-400';
      case 'platinum': return 'from-purple-400 to-pink-400';
      default: return 'from-gray-600 to-gray-500';
    }
  };

  const getTierIcon = (tier) => {
    switch (tier) {
      case 'bronze': return <Star className="w-6 h-6" />;
      case 'silver': return <Award className="w-6 h-6" />;
      case 'gold': return <Gift className="w-6 h-6" />;
      case 'platinum': return <Coins className="w-6 h-6" />;
      default: return <Star className="w-6 h-6" />;
    }
  };

  if (loading) {
    return (
      <div className="relative z-10 min-h-screen pt-24 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  const progressToNext = loyalty?.next_tier
    ? ((loyalty.points - (loyalty.tiers[loyalty.tier]?.min_points || 0)) /
       (loyalty.next_tier.points_needed + loyalty.points - (loyalty.tiers[loyalty.tier]?.min_points || 0))) * 100
    : 100;

  return (
    <div className="relative z-10 min-h-screen pt-24 pb-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white mb-4">Loyalty Rewards</h1>
          <p className="text-gray-400">Earn points on every booking and redeem for discounts</p>
        </div>

        {/* Current Status Card */}
        <div className={`rounded-2xl bg-gradient-to-r ${getTierColor(loyalty?.tier)} p-1 mb-8`}>
          <div className="bg-gray-900 rounded-xl p-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className={`w-16 h-16 rounded-full bg-gradient-to-r ${getTierColor(loyalty?.tier)} flex items-center justify-center text-white`}>
                  {getTierIcon(loyalty?.tier)}
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Current Tier</p>
                  <h2 className="text-2xl font-bold text-white capitalize">{loyalty?.tier}</h2>
                  <p className="text-purple-400">{loyalty?.discount}% discount on bookings</p>
                </div>
              </div>
              <div className="text-center md:text-right">
                <p className="text-gray-400 text-sm">Available Points</p>
                <p className="text-4xl font-bold text-white">{loyalty?.points?.toLocaleString()}</p>
              </div>
            </div>

            {loyalty?.next_tier && (
              <div className="mt-6">
                <div className="flex justify-between text-sm text-gray-400 mb-2">
                  <span className="capitalize">{loyalty.tier}</span>
                  <span className="capitalize">{loyalty.next_tier.name}</span>
                </div>
                <div className="h-3 bg-gray-800 rounded-full overflow-hidden">
                  <div
                    className={`h-full bg-gradient-to-r ${getTierColor(loyalty.next_tier.name)} transition-all duration-500`}
                    style={{ width: `${progressToNext}%` }}
                  />
                </div>
                <p className="text-sm text-gray-400 mt-2">
                  {loyalty.next_tier.points_needed.toLocaleString()} more points to {loyalty.next_tier.name}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white/5 rounded-xl p-4 text-center">
            <TrendingUp className="w-6 h-6 text-green-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-white">{loyalty?.total_earned?.toLocaleString()}</p>
            <p className="text-sm text-gray-400">Total Earned</p>
          </div>
          <div className="bg-white/5 rounded-xl p-4 text-center">
            <Gift className="w-6 h-6 text-purple-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-white">{loyalty?.total_redeemed?.toLocaleString()}</p>
            <p className="text-sm text-gray-400">Total Redeemed</p>
          </div>
          <div className="bg-white/5 rounded-xl p-4 text-center">
            <Coins className="w-6 h-6 text-yellow-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-white">${(loyalty?.points / 100).toFixed(2)}</p>
            <p className="text-sm text-gray-400">Point Value</p>
          </div>
          <div className="bg-white/5 rounded-xl p-4 text-center">
            <Award className="w-6 h-6 text-blue-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-white">{loyalty?.discount}%</p>
            <p className="text-sm text-gray-400">Your Discount</p>
          </div>
        </div>

        {/* Redeem Section */}
        <div className="bg-white/5 rounded-xl p-6 mb-8">
          <h3 className="text-xl font-bold text-white mb-4">Redeem Points</h3>
          <p className="text-gray-400 mb-4">100 points = $1.00 discount on your next booking</p>
          <div className="flex flex-col sm:flex-row gap-4">
            <input
              type="number"
              value={redeemPoints}
              onChange={e => setRedeemPoints(parseInt(e.target.value) || 0)}
              min={100}
              step={100}
              max={loyalty?.points}
              className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
            />
            <Button onClick={handleRedeem} disabled={redeeming || redeemPoints > loyalty?.points}>
              Redeem ${(redeemPoints / 100).toFixed(2)}
            </Button>
          </div>
        </div>

        {/* Tier Benefits */}
        <div className="bg-white/5 rounded-xl p-6">
          <h3 className="text-xl font-bold text-white mb-4">Tier Benefits</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {loyalty?.tiers && Object.entries(loyalty.tiers).map(([tier, info]) => (
              <div
                key={tier}
                className={`p-4 rounded-lg border ${loyalty.tier === tier ? 'border-purple-500 bg-purple-500/10' : 'border-gray-700'}`}
              >
                <div className={`w-10 h-10 rounded-full bg-gradient-to-r ${getTierColor(tier)} flex items-center justify-center text-white mb-3`}>
                  {getTierIcon(tier)}
                </div>
                <h4 className="font-bold text-white capitalize">{tier}</h4>
                <p className="text-sm text-gray-400">{info.min_points.toLocaleString()}+ points</p>
                <p className="text-purple-400 font-semibold">{info.discount}% discount</p>
              </div>
            ))}
          </div>
        </div>

        {/* How to Earn */}
        <div className="mt-8 text-center">
          <h3 className="text-lg font-bold text-white mb-2">How to Earn Points</h3>
          <p className="text-gray-400">Earn 10 points for every $1 spent on bookings. Refer friends for bonus points!</p>
        </div>
      </div>
    </div>
  );
};

export default LoyaltyProgram;